import {
  CartErrorEvent,
  CartUpdateEvent,
  ThemeEvents,
} from './events.js';
import { cartPerformance } from './performance-component.js';
import { morphSection } from './section-renderer.js';

const selectors = {
  cartChange: '[data-cart-change]',
  cartCount: '[data-cart-count]',
  cartCountLive: '[data-cart-count-live]',
  cartLabel: '[data-cart-label]',
  cartOpen: '[data-cart-drawer-open]',
  cartLine: '[data-cart-line-row]',
  cartSection: '[data-cart-drawer-section], [data-cart-page-section]',
};

const routesRoot = window.Shopify?.routes?.root || '/';
const quantityUpdateTimers = new Map();

/* A healthy cart needs at most one gift write per customer action. More than
   this many inside the window means two rules disagree, so stop writing rather
   than flicker the drawer and hammer the Cart API. */
const freeGiftThrottle = { limitPerWindow: 6, windowMs: 10000, writes: [] };
let freeGiftOperationInProgress = false;
let freeGiftSyncQueued = false;
let freeGiftSyncRequestedDuringOperation = false;
let freeGiftThrottleReported = false;

function updateCartCount(count) {
  if (count === undefined || count === null || Number.isNaN(Number(count))) return;

  document.querySelectorAll(selectors.cartCount).forEach((node) => {
    node.textContent = String(count);
  });
  document.querySelectorAll(selectors.cartCountLive).forEach((node) => {
    const label = node.closest(selectors.cartOpen)?.querySelector(selectors.cartLabel)?.textContent?.trim() || 'Cart';
    node.textContent = `${label} - ${count}`;
  });
  document.querySelectorAll(selectors.cartOpen).forEach((node) => {
    const label = node.querySelector(selectors.cartLabel)?.textContent?.trim() || 'Cart';
    node.setAttribute('aria-label', `${label} - ${count}`);
  });
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll(selectors.cartSection))
    .map((section) => section.dataset.sectionId)
    .filter(Boolean)
    .filter((sectionId, index, sectionIds) => sectionIds.indexOf(sectionId) === index);
}

function cartSectionPayload(extra = {}) {
  const sectionIds = cartSectionIds();
  if (sectionIds.length === 0) return extra;

  const sectionsUrl = new URL(window.location.href);
  sectionsUrl.searchParams.delete('sections');
  sectionsUrl.searchParams.delete('section_id');

  return {
    ...extra,
    sections: sectionIds.join(','),
    sections_url: `${sectionsUrl.pathname}${sectionsUrl.search}`,
  };
}

function sectionMode(sectionId) {
  const section = document.querySelector(`[data-section-id="${CSS.escape(sectionId)}"]`);
  return section?.matches('[data-cart-drawer-section]') ? 'hydration' : 'full';
}

function itemCountFromRenderedSections() {
  const counts = Array.from(document.querySelectorAll('[data-cart-item-count]'))
    .map((node) => Number(node.dataset.cartItemCount))
    .filter((count) => !Number.isNaN(count));
  return counts.length > 0 ? counts[0] : undefined;
}

/* Read the count straight out of the section HTML the Section Rendering API just
   returned. Two things make this the only reliable source after an add:
   /cart/add.js returns the line item, which carries no item_count, and
   data-cart-item-count lives on the cart-drawer section root — which hydration
   morphing leaves in place, so the rendered DOM still reports the pre-add count. */
function itemCountFromSectionHtml(sections = {}) {
  for (const html of Object.values(sections || {})) {
    if (typeof html !== 'string') continue;

    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-item-count]');
    if (!node) continue;

    const count = Number(node.dataset.cartItemCount);
    if (!Number.isNaN(count)) return count;
  }

  return undefined;
}

function applyCartSections(sections = {}) {
  const renderedCount = itemCountFromSectionHtml(sections);

  Object.entries(sections || {}).forEach(([sectionId, html]) => {
    morphSection(sectionId, html, sectionMode(sectionId));
  });

  updateCartCount(renderedCount ?? itemCountFromRenderedSections());
}

async function renderKnownCartSections() {
  await Promise.all(
    cartSectionIds().map((sectionId) => window.MedistikSectionRenderer?.sectionRenderer.renderSection(sectionId, {
      mode: sectionMode(sectionId),
      cache: false,
    })),
  );
  updateCartCount(itemCountFromRenderedSections());
}

function cartErrorSurface(button) {
  return button.closest(selectors.cartSection);
}

function currentCartErrorSurface(fallback = null) {
  return fallback?.isConnected ? fallback : document.querySelector(selectors.cartSection);
}

function setCartChangeError(surface, message) {
  if (!surface) return;

  let error = surface.querySelector('[data-cart-change-error]');
  if (!message) {
    error?.remove();
    return;
  }

  if (!error) {
    error = document.createElement('p');
    error.className = 'md-form-error';
    error.setAttribute('role', 'alert');
    error.dataset.cartChangeError = '';

    const target = surface.querySelector('[data-cart-drawer-items]') || surface.querySelector('.md-cart__inner') || surface;
    target.prepend(error);
  }

  error.textContent = message;
}

function setCartChangeBusy(node, busy) {
  if (!node) return;
  node.toggleAttribute('aria-busy', busy);
  if ('disabled' in node) node.disabled = busy;
}

function setLineRemoving(node, removing) {
  const line = node?.closest(selectors.cartLine);
  if (!line) return;

  line.toggleAttribute('data-cart-line-removing', removing);
  line.toggleAttribute('aria-busy', removing);
}

async function cartChangeErrorFromResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const cartError = new Error('Unable to update cart.');
  cartError.cartStatus = response.status;

  if (contentType.includes('application/json')) {
    try {
      const error = await response.json();
      cartError.message = error.description || error.message || cartError.message;
      cartError.errors = error.errors || null;
    } catch (error) {
      /* Keep the generic message when Shopify returns malformed JSON. */
    }
    return cartError;
  }

  if (contentType.includes('text/html')) {
    const body = await response.text().catch(() => '');
    const verificationPage = /verifying your connection/i.test(body);
    if (verificationPage || response.status === 401 || response.status === 403) {
      cartError.message = 'Unable to update cart. Storefront verification blocked the cart request.';
    }
  }

  return cartError;
}

function freeGiftState() {
  const stateNode = document.querySelector('[data-free-gift-state]');
  const cartDrawer = document.querySelector('[data-cart-drawer-section]');
  if (!stateNode || !cartDrawer) return null;

  return {
    cartDrawer,
    enabled: stateNode.dataset.freeGiftEnabled === 'true',
    threshold: Number(stateNode.dataset.freeGiftThreshold || 0),
    variantId: stateNode.dataset.freeGiftVariantId || '',
    giftLineKey: stateNode.dataset.freeGiftLineKey || '',
    giftLineVariantId: stateNode.dataset.freeGiftLineVariantId || '',
    giftLineQuantity: Number(stateNode.dataset.freeGiftLineQuantity || 0),
    extraGiftLineKeys: (stateNode.dataset.freeGiftExtraKeys || '').split(',').filter(Boolean),
    qualifyingTotal: Number(stateNode.dataset.cartQualifyingTotal || 0),
  };
}

function freeGiftWriteAllowed() {
  const now = Date.now();
  const { writes, windowMs, limitPerWindow } = freeGiftThrottle;

  while (writes.length > 0 && now - writes[0] > windowMs) writes.shift();
  if (writes.length >= limitPerWindow) {
    if (!freeGiftThrottleReported) {
      freeGiftThrottleReported = true;
      console.warn('Free gift sync paused: too many cart writes in a row.');
    }
    return false;
  }

  writes.push(now);
  return true;
}

function setFreeGiftSyncing(syncing) {
  /* On the root element, because the cart sections are morphed mid-flight and
     would drop an attribute set on the drawer itself. */
  document.documentElement.toggleAttribute('data-free-gift-syncing', syncing);
}

async function syncFreeGift() {
  if (freeGiftOperationInProgress) {
    freeGiftSyncRequestedDuringOperation = true;
    return;
  }

  const state = freeGiftState();
  if (!state) return;

  /* A gift programme that is off for the active currency stays inert: it never
     adds, and it never removes a gift the customer already earned. Deleting
     here is what made gifts vanish on any render outside CAD. */
  if (!state.enabled || !state.variantId || state.variantId === '0') return;

  const qualifies = state.qualifyingTotal >= state.threshold;
  const giftProductChanged =
    state.giftLineKey &&
    state.giftLineVariantId &&
    state.giftLineVariantId !== state.variantId;
  const duplicateGiftLineKey = state.extraGiftLineKeys[0];
  const shouldRemove = Boolean(state.giftLineKey) && (!qualifies || giftProductChanged);
  const shouldAdd = qualifies && !state.giftLineKey;
  const shouldNormalizeQuantity = qualifies && Boolean(state.giftLineKey) && !giftProductChanged && state.giftLineQuantity !== 1;

  if (!duplicateGiftLineKey && !shouldRemove && !shouldAdd && !shouldNormalizeQuantity) return;
  if (!freeGiftWriteAllowed()) return;

  /* Duplicates are pruned one per pass; the re-render queues the next one. */
  const endpoint = shouldAdd && !duplicateGiftLineKey ? 'cart/add.js' : 'cart/change.js';
  let payload;
  if (duplicateGiftLineKey) {
    payload = cartSectionPayload({ id: duplicateGiftLineKey, quantity: 0 });
  } else if (shouldAdd) {
    payload = cartSectionPayload({
      items: [
        {
          id: state.variantId,
          quantity: 1,
          properties: { _free_gift: 'true' },
        },
      ],
    });
  } else {
    payload = cartSectionPayload({ id: state.giftLineKey, quantity: shouldRemove ? 0 : 1 });
  }

  /* Set last, so nothing between here and the finally block can strand the
     in-progress flag and kill the engine for the rest of the page's life. */
  freeGiftOperationInProgress = true;
  setFreeGiftSyncing(true);
  setCartChangeError(state.cartDrawer, '');

  try {
    const response = await fetch(`${routesRoot}${endpoint}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw await cartChangeErrorFromResponse(response);

    const result = await response.json();
    document.dispatchEvent(
      new CartUpdateEvent(result, 'cart-free-gift', {
        source: 'cart-free-gift',
        sections: result.sections || {},
        itemCount: result.item_count,
      }),
    );
  } catch (error) {
    const message = error.message || 'Unable to update cart.';
    setCartChangeError(currentCartErrorSurface(state.cartDrawer), message);
    document.dispatchEvent(new CartErrorEvent('cart-free-gift', message, error.description, error.errors));
  } finally {
    freeGiftOperationInProgress = false;
    setFreeGiftSyncing(false);

    /* Re-run instead of dropping the state that changed mid-flight, otherwise
       the gift can sit unadded until the next customer action. */
    if (freeGiftSyncRequestedDuringOperation) {
      freeGiftSyncRequestedDuringOperation = false;
      queueFreeGiftSync();
    }
  }
}

function queueFreeGiftSync() {
  if (freeGiftSyncQueued) return;
  freeGiftSyncQueued = true;

  queueMicrotask(() => {
    freeGiftSyncQueued = false;
    syncFreeGift();
  });
}

function mutationChangesFreeGiftState(mutation) {
  if (mutation.type === 'attributes') {
    return mutation.target instanceof Element && mutation.target.matches('[data-free-gift-state]');
  }

  return [...mutation.addedNodes, ...mutation.removedNodes].some(
    (node) =>
      node instanceof Element &&
      (node.matches('[data-free-gift-state]') || Boolean(node.querySelector('[data-free-gift-state]'))),
  );
}

function observeFreeGiftState() {
  const cartDrawer = document.querySelector('[data-cart-drawer-section]');
  if (!cartDrawer || typeof MutationObserver === 'undefined') return;

  const observer = new MutationObserver((mutations) => {
    if (mutations.some(mutationChangesFreeGiftState)) queueFreeGiftSync();
  });
  observer.observe(cartDrawer, {
    attributes: true,
    attributeFilter: [
      'data-cart-qualifying-total',
      'data-free-gift-enabled',
      'data-free-gift-threshold',
      'data-free-gift-variant-id',
      'data-free-gift-line-key',
      'data-free-gift-line-variant-id',
      'data-free-gift-line-quantity',
      'data-free-gift-extra-keys',
    ],
    childList: true,
    subtree: true,
  });
}

function sourceIdFor(node) {
  return node.id || node.closest('[id]')?.id || 'cart-items';
}

/**
 * @param {Element} trigger - Control that requested the change.
 * @param {string} lineKey - Cart line item key (never a line index: the free
 *   gift can be added or removed between queueing and sending, which shifts
 *   every index and would rewrite the wrong line).
 * @param {number} quantity - Target quantity, 0 to remove.
 */
async function changeCartLine(trigger, lineKey, quantity) {
  if (!lineKey || Number.isNaN(quantity)) return;
  if (trigger?.getAttribute('aria-busy') === 'true') return;

  const sourceId = sourceIdFor(trigger);
  const errorSurface = cartErrorSurface(trigger);
  setCartChangeError(errorSurface, '');
  setCartChangeBusy(trigger, true);
  if (quantity === 0) setLineRemoving(trigger, true);

  const requestMarker = cartPerformance.createStartingMarker('change:request');
  const payload = cartSectionPayload({ id: lineKey, quantity });

  try {
    const response = await fetch(`${routesRoot}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw await cartChangeErrorFromResponse(response);

    const result = await response.json();
    cartPerformance.measureFromMarker(requestMarker);
    document.dispatchEvent(
      new CartUpdateEvent(result, sourceId, {
        source: 'cart-change',
        sections: result.sections || {},
        itemCount: result.item_count,
        line: lineKey,
        quantity,
      }),
    );
  } catch (error) {
    const message = error.message || 'Unable to update cart.';
    await renderKnownCartSections().catch(() => {});
    setCartChangeError(currentCartErrorSurface(errorSurface), message);
    document.dispatchEvent(new CartErrorEvent(sourceId, message, error.description, error.errors));
  } finally {
    setCartChangeBusy(trigger, false);
    setLineRemoving(trigger, false);
  }
}

function queueQuantityChange(trigger, line, quantity) {
  const key = `${line}`;
  if (quantity === 0) setLineRemoving(trigger, true);
  window.clearTimeout(quantityUpdateTimers.get(key));
  quantityUpdateTimers.set(
    key,
    window.setTimeout(() => {
      quantityUpdateTimers.delete(key);
      changeCartLine(trigger, line, quantity);
    }, 300),
  );
}

function handleCartEvent(event) {
  const sections = event.detail?.data?.sections;
  const itemCount = event.detail?.data?.itemCount ?? event.detail?.resource?.item_count;

  if (sections && Object.keys(sections).length > 0) {
    applyCartSections(sections);
  } else {
    renderKnownCartSections().catch(() => {});
  }

  updateCartCount(itemCount);
  queueFreeGiftSync();
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartEvent);
document.addEventListener(ThemeEvents.discountUpdate, handleCartEvent);

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const { cartLine, quantity } = event.detail || {};
  if (!cartLine) return;
  cartPerformance.measureFromEvent('change:user-action', event);
  const trigger = event.target instanceof Element ? event.target : document.getElementById(event.detail.sourceId) || document.activeElement;
  queueQuantityChange(trigger, cartLine, Number(quantity));
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const changeButton = event.target.closest(selectors.cartChange);
  if (changeButton) {
    event.preventDefault();
    const line = changeButton.dataset.line;
    const quantity = Number(changeButton.dataset.quantity);
    queueQuantityChange(changeButton, line, quantity);
    return;
  }
});

queueFreeGiftSync();
observeFreeGiftState();

window.MedistikCartItems = {
  changeLine: changeCartLine,
  updateCartCount,
  applyCartSections,
};
